import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo, { LogoMark } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { House, Users, GraduationCap, Receipt, Wallet, Bank, ChartPieSlice, ListChecks, Gear, SignOut, UsersThree, HandCoins, TestTube, IdentificationCard, CaretLeft, CaretRight, Student } from "@phosphor-icons/react";

const NAV = {
  school: [
    { to: "/school", label: "Overview", icon: House },
    { to: "/school/students", label: "Students", icon: Users },
    { to: "/school/fees", label: "Fee Structure", icon: GraduationCap },
    { to: "/school/transactions", label: "Collections", icon: Receipt },
  ],
  parent: [
    { to: "/parent", label: "Home", icon: House },
    { to: "/parent/pay", label: "Pay School Fees", icon: Bank },
    { to: "/parent/loans", label: "School-Fee Loans", icon: HandCoins },
    { to: "/parent/academic-advance", label: "Academic Liquidity Advance", icon: Student },
  ],
  agent: [
    { to: "/agent", label: "DSA Console", icon: ChartPieSlice },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: ChartPieSlice },
    { to: "/admin/reviewers", label: "Credit reviewers", icon: ListChecks },
    { to: "/admin/loans", label: "Disburse queue", icon: HandCoins },
    { to: "/admin/ala-payouts", label: "ALA payouts", icon: HandCoins },
    { to: "/admin/kyc", label: "KYC records", icon: IdentificationCard },
    { to: "/admin/stress-test", label: "Stress test", icon: TestTube },
    { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    { to: "/admin/users", label: "Users", icon: UsersThree },
    { to: "/admin/fee-config", label: "Fee config", icon: Gear },
  ],
  credit_reviewer: [
    { to: "/approver", label: "Overview", icon: ChartPieSlice },
    { to: "/approver/queue", label: "My queue", icon: ListChecks },
    { to: "/approver/loans", label: "Direct Debit checks", icon: Bank },
  ],
};

const ROLE_LABEL = { school: "School Portal", parent: "Parent", agent: "Agent / DSA", admin: "Admin", credit_reviewer: "Credit Reviewer" };

export default function DashboardShell({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  if (!user) return null;
  const items = NAV[user.role] || [];
  const isParent = user.role === "parent";

  return (
    <div className="min-h-screen flex bg-[#F9F7F4]">
      {/* Sidebar */}
      <aside className={`${isParent ? "hidden lg:flex" : "flex"} flex-col w-64 bg-[#0A3622] text-white sticky top-0 h-screen`}>
        <div className="px-6 py-6 border-b border-white/10">
          <Logo variant="dark" testid="dashboard-logo" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-4" data-testid="dashboard-role-label">{ROLE_LABEL[user.role]}</div>
        </div>
        <nav className="flex-1 px-4 py-5 space-y-1">
          {items.map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to || (to !== "/" + user.role && loc.pathname.startsWith(to));
            return (
              <Link key={to} to={to} className={`sidebar-link ${active ? "active" : ""}`} data-testid={`sidebar-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={18} weight={active ? "fill" : "regular"} />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#F2B632]/20 grid place-items-center text-[#F2B632] font-semibold font-display">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-white/50 truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={async () => { await logout(); nav("/"); }} className="sidebar-link w-full mt-1" data-testid="logout-button">
            <SignOut size={18} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E5E3DB] sticky top-0 z-30">
          <div className="px-6 md:px-10 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {isParent && (
                <Link to="/" aria-label="Ascentremit home" className="lg:hidden shrink-0">
                  <LogoMark testid="dashboard-mobile-mark" />
                </Link>
              )}
              {/* History navigation arrows — browser-level back/forward within the SPA */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => nav(-1)}
                  className="w-8 h-8 rounded-full border border-[#E5E3DB] hover:border-[#0A3622] hover:text-[#0A3622] text-[#4F5D4F] grid place-items-center transition-colors"
                  aria-label="Go back"
                  data-testid="nav-back-button"
                >
                  <CaretLeft size={14} weight="bold" />
                </button>
                <button
                  onClick={() => nav(1)}
                  className="w-8 h-8 rounded-full border border-[#E5E3DB] hover:border-[#0A3622] hover:text-[#0A3622] text-[#4F5D4F] grid place-items-center transition-colors"
                  aria-label="Go forward"
                  data-testid="nav-forward-button"
                >
                  <CaretRight size={14} weight="bold" />
                </button>
              </div>
              <div className="min-w-0">
                <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight truncate" data-testid="page-title">{title}</h1>
                {subtitle && <p className="text-xs text-[#4F5D4F] mt-0.5 truncate">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {actions}
              {user.role === "admin" && <NotificationBell />}
              {/* Always-visible header logout (mobile parent has no sidebar) */}
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#4F5D4F] hover:text-[#C93B3B] hover:bg-[#FBE3E3] transition-colors"
                aria-label="Sign out"
                data-testid="header-logout-button"
              >
                <SignOut size={14} weight="bold" />
                <span className="hidden md:inline">Sign out</span>
              </button>
            </div>
          </div>
        </header>
        <div className="px-6 md:px-10 py-8">{children}</div>

        {/* Mobile bottom nav for parent */}
        {isParent && (
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E3DB] grid grid-cols-4 py-2 z-30">
            {items.map(({ to, label, icon: Icon }) => {
              const active = loc.pathname === to;
              return (
                <Link key={to} to={to} className={`flex flex-col items-center justify-center text-[10px] gap-1 py-1 ${active ? "text-[#F2B632]" : "text-[#4F5D4F]"}`} data-testid={`bottom-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon size={20} weight={active ? "fill" : "regular"} />
                  <span>{label.split(" ")[0]}</span>
                </Link>
              );
            })}
          </nav>
        )}
      </main>
    </div>
  );
}

export function StatCard({ label, value, sub, accent = "primary" }) {
  const colors = {
    primary: "text-[#0A3622]",
    accent: "text-[#F2B632]",
    success: "text-[#2D7A5D]",
    warning: "text-[#B45309]",
  };
  return (
    <div className="card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-xs uppercase tracking-[0.16em] text-[#4F5D4F] font-semibold">{label}</p>
      <p className={`font-display text-3xl mt-2 font-semibold ${colors[accent]}`}>{value}</p>
      {sub && <p className="text-xs text-[#4F5D4F] mt-1">{sub}</p>}
    </div>
  );
}
